<script>
import { useAlert } from 'dashboard/composables';
import BaseModal from 'dashboard/components/dahreply/BaseModal.vue';
import DeleteModal from 'dashboard/components/dahreply/DeleteModal.vue';
import PaginationBar from 'dashboard/components/dahreply/PaginationBar.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import {
  listDocuments,
  updateDocument,
  deleteDocument,
  getDocumentStatus,
} from '../../../../api/coLiving/documents';
import { apiErrorMessage } from '../../../../api/coLiving/errors';
import { FIELD_CLASS, SELECT_CLASS } from '../constants';
import { formatTimestamp } from '../ticketHelpers';
import {
  DOC_SORTS,
  DOC_STATUS_STYLE,
  formatBytes,
  processingIds,
} from '../documents';
import DocumentUploadModal from './DocumentUploadModal.vue';
import FormField from './FormField.vue';

const PAGE_SIZE_OPTIONS = [10, 20, 50];
const SEARCH_DELAY_MS = 300;
const POLL_MS = 4000;

// One tab of Condo Details (Notice, MOM or House Rules): the condo's own files.
// A file is always kept privately; with "Visible to the bot" on it is also sent
// to the condo's knowledge base so the tenant chat can answer from it. Rows
// that are still being read are polled until they are ready or failed.
export default {
  name: 'CoLivingCondoDocuments',
  components: {
    BaseModal,
    DeleteModal,
    PaginationBar,
    LoadingState,
    DocumentUploadModal,
    FormField,
  },
  props: {
    condoId: { type: String, required: true },
    // API type: notice | mom | house_rules.
    type: { type: String, required: true },
    // The condo's account timezone for the dates.
    timezone: { type: String, default: '' },
  },
  data() {
    return {
      fieldClass: FIELD_CLASS,
      selectClass: SELECT_CLASS,
      sorts: DOC_SORTS,
      statusStyle: DOC_STATUS_STYLE,
      pageSizeOptions: PAGE_SIZE_OPTIONS,
      items: [],
      total: 0,
      page: 1,
      pageSize: PAGE_SIZE_OPTIONS[0],
      search: '',
      sort: 'newest',
      isLoading: false,
      loadError: '',
      uploadOpen: false,
      renaming: null,
      renameTitle: '',
      isRenaming: false,
      renameError: '',
      toDelete: null,
      isDeleting: false,
      // ids with a switch change in flight
      busy: {},
      searchTimer: null,
      pollTimer: null,
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.pageSize));
    },
    pagination() {
      return {
        currentPage: this.page,
        totalPages: this.totalPages,
        pageSize: this.pageSize,
      };
    },
  },
  watch: {
    // Switching tabs reuses the component.
    type() {
      this.page = 1;
      this.search = '';
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
    clearTimeout(this.pollTimer);
  },
  methods: {
    formatTimestamp,
    formatBytes,
    async load() {
      this.isLoading = true;
      this.loadError = '';
      try {
        const { data } = await listDocuments(this.condoId, {
          type: this.type,
          page: this.page,
          page_size: this.pageSize,
          sort: this.sort,
          ...(this.search.trim() ? { search: this.search.trim() } : {}),
        });
        this.items = data.results;
        this.total = data.total;
        this.schedulePoll();
      } catch (err) {
        this.loadError = apiErrorMessage(
          this,
          err,
          'CO_LIVING.COMMON.LOAD_FAILED'
        );
      } finally {
        this.isLoading = false;
      }
    },
    onFilterChange() {
      this.page = 1;
      this.load();
    },
    onSearchInput() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.onFilterChange, SEARCH_DELAY_MS);
    },
    goToPage(page) {
      this.page = page;
      this.load();
    },
    onPageSizeChange(size) {
      this.pageSize = size;
      this.onFilterChange();
    },
    // Rows still "processing" are asked about every few seconds; the server
    // also settles them on its own, so this only keeps the screen current.
    schedulePoll() {
      clearTimeout(this.pollTimer);
      if (!processingIds(this.items).length) return;
      this.pollTimer = setTimeout(this.poll, POLL_MS);
    },
    async poll() {
      const ids = processingIds(this.items);
      await Promise.all(
        ids.map(async id => {
          try {
            const { data } = await getDocumentStatus(this.condoId, id);
            const row = this.items.find(item => item.id === id);
            if (row) {
              row.status = data.status;
              row.error = data.error;
              row.word_count = data.word_count;
            }
          } catch (err) {
            // keep the row as it is; the next round asks again
          }
        })
      );
      this.schedulePoll();
    },
    async onUploaded() {
      await this.load();
    },
    async toggleBot(doc) {
      this.$set(this.busy, doc.id, true);
      try {
        const { data } = await updateDocument(this.condoId, doc.id, {
          bot_visible: !doc.bot_visible,
        });
        Object.assign(doc, data);
        useAlert(
          this.$t(
            data.bot_visible
              ? 'CO_LIVING.CONDOS.DOCUMENTS.BOT_ON'
              : 'CO_LIVING.CONDOS.DOCUMENTS.BOT_OFF'
          )
        );
        this.schedulePoll();
      } catch (err) {
        useAlert(
          apiErrorMessage(this, err, 'CO_LIVING.CONDOS.DOCUMENTS.BOT_FAILED')
        );
      } finally {
        this.$set(this.busy, doc.id, false);
      }
    },
    openRename(doc) {
      this.renaming = doc;
      this.renameTitle = doc.title;
      this.renameError = '';
    },
    async onRename() {
      const title = this.renameTitle.trim();
      if (!title) {
        this.renameError = this.$t('CO_LIVING.CONDOS.DOCUMENTS.TITLE_REQUIRED');
        return;
      }
      this.isRenaming = true;
      try {
        const { data } = await updateDocument(this.condoId, this.renaming.id, {
          title,
        });
        Object.assign(this.renaming, data);
        useAlert(this.$t('CO_LIVING.CONDOS.DOCUMENTS.RENAMED'));
        this.renaming = null;
      } catch (err) {
        this.renameError = apiErrorMessage(
          this,
          err,
          'CO_LIVING.CONDOS.DOCUMENTS.RENAME_FAILED'
        );
      } finally {
        this.isRenaming = false;
      }
    },
    async confirmDelete() {
      this.isDeleting = true;
      try {
        await deleteDocument(this.condoId, this.toDelete.id);
        useAlert(this.$t('CO_LIVING.CONDOS.DOCUMENTS.DELETED'));
        this.toDelete = null;
        this.load();
      } catch (err) {
        useAlert(
          apiErrorMessage(this, err, 'CO_LIVING.CONDOS.DOCUMENTS.DELETE_FAILED')
        );
      } finally {
        this.isDeleting = false;
      }
    },
    statusLabel(doc) {
      return this.$t(
        `CO_LIVING.CONDOS.DOCUMENTS.STATUS.${doc.status.toUpperCase()}`
      );
    },
  },
};
</script>

<template>
  <section data-testid="co-living-condo-documents">
    <div class="flex flex-wrap items-start justify-between gap-4 mb-3">
      <p class="text-sm text-slate-600 dark:text-slate-300">
        {{ $t('CO_LIVING.CONDOS.DOCUMENTS.INTRO') }}
      </p>
      <woot-button
        icon="upload"
        data-testid="co-living-document-upload"
        @click="uploadOpen = true"
      >
        {{ $t('CO_LIVING.CONDOS.DOCUMENTS.UPLOAD_TITLE') }}
      </woot-button>
    </div>

    <div class="flex flex-wrap gap-3 mb-3">
      <input
        v-model="search"
        type="search"
        :placeholder="$t('CO_LIVING.CONDOS.DOCUMENTS.SEARCH')"
        class="max-w-xs"
        :class="[fieldClass]"
        data-testid="co-living-document-search"
        @input="onSearchInput"
      />
      <select
        v-model="sort"
        class="max-w-xs"
        :class="[selectClass]"
        data-testid="co-living-document-sort"
        @change="onFilterChange"
      >
        <option v-for="s in sorts" :key="s" :value="s">
          {{ $t(`CO_LIVING.CONDOS.DOCUMENTS.SORTS.${s.toUpperCase()}`) }}
        </option>
      </select>
    </div>

    <LoadingState
      v-if="isLoading && !items.length"
      :message="$t('CO_LIVING.COMMON.LOADING')"
    />
    <p
      v-else-if="loadError"
      class="text-sm text-red-500"
      data-testid="co-living-document-load-error"
    >
      {{ loadError }}
    </p>
    <p
      v-else-if="!items.length"
      class="text-sm text-slate-500 dark:text-slate-400"
      data-testid="co-living-documents-empty"
    >
      {{
        search.trim()
          ? $t('CO_LIVING.CONDOS.DOCUMENTS.NO_MATCH')
          : $t('CO_LIVING.CONDOS.DOCUMENTS.EMPTY')
      }}
    </p>
    <template v-else>
      <div
        class="overflow-x-auto bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg"
      >
        <table class="w-full text-sm" data-testid="co-living-documents-table">
          <thead>
            <tr class="text-left text-slate-500 dark:text-slate-400">
              <th class="p-3">
                {{ $t('CO_LIVING.CONDOS.DOCUMENTS.COLUMNS.NAME') }}
              </th>
              <th class="p-3">
                {{ $t('CO_LIVING.CONDOS.DOCUMENTS.COLUMNS.UPLOADED') }}
              </th>
              <th class="p-3">
                {{ $t('CO_LIVING.CONDOS.DOCUMENTS.COLUMNS.BOT') }}
              </th>
              <th class="p-3" />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="doc in items"
              :key="doc.id"
              class="border-t border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100"
              :data-testid="`co-living-document-${doc.id}`"
            >
              <td class="p-3">
                {{ doc.title }}
                <span class="block text-xs text-slate-500">
                  {{ doc.file_name }} · {{ formatBytes(doc.size) }}
                </span>
              </td>
              <td class="p-3">
                {{ formatTimestamp(doc.created_at, timezone) }}
              </td>
              <td class="p-3">
                <label class="flex items-center gap-2">
                  <input
                    type="checkbox"
                    :checked="doc.bot_visible"
                    :disabled="busy[doc.id]"
                    :data-testid="`co-living-document-bot-${doc.id}`"
                    @change="toggleBot(doc)"
                  />
                  <span
                    class="px-2 py-0.5 text-xs font-medium rounded"
                    :class="statusStyle[doc.status]"
                    :data-testid="`co-living-document-status-${doc.id}`"
                  >
                    {{ statusLabel(doc) }}
                  </span>
                </label>
                <p
                  v-if="doc.status === 'failed' && doc.error"
                  class="mt-1 text-xs text-red-500"
                >
                  {{ doc.error }}
                </p>
              </td>
              <td class="p-3 text-right whitespace-nowrap">
                <a
                  v-if="doc.url"
                  v-tooltip.top="$t('CO_LIVING.CONDOS.DOCUMENTS.DOWNLOAD')"
                  :href="doc.url"
                  :aria-label="$t('CO_LIVING.CONDOS.DOCUMENTS.DOWNLOAD')"
                  class="inline-flex p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700"
                  :data-testid="`co-living-document-download-${doc.id}`"
                >
                  <fluent-icon icon="arrow-download" size="16" />
                </a>
                <woot-button
                  v-tooltip.top="$t('CO_LIVING.COMMON.EDIT')"
                  variant="clear"
                  size="small"
                  color-scheme="secondary"
                  icon="edit"
                  :aria-label="$t('CO_LIVING.COMMON.EDIT')"
                  :data-testid="`co-living-document-rename-${doc.id}`"
                  @click="openRename(doc)"
                />
                <woot-button
                  v-tooltip.top="$t('CO_LIVING.COMMON.DELETE')"
                  variant="clear"
                  size="small"
                  color-scheme="alert"
                  icon="delete"
                  :aria-label="$t('CO_LIVING.COMMON.DELETE')"
                  :data-testid="`co-living-document-delete-${doc.id}`"
                  @click="toDelete = doc"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <PaginationBar
        :pagination="pagination"
        :page-size-options="pageSizeOptions"
        :loading="isLoading"
        @previous="goToPage(page - 1)"
        @next="goToPage(page + 1)"
        @page-size-change="onPageSizeChange"
      />
    </template>

    <DocumentUploadModal
      v-if="uploadOpen"
      :condo-id="condoId"
      :type="type"
      @close="uploadOpen = false"
      @uploaded="onUploaded"
    />

    <BaseModal v-if="renaming" @close="renaming = null">
      <template #title>
        {{ $t('CO_LIVING.CONDOS.DOCUMENTS.RENAME_TITLE') }}
      </template>
      <form
        class="grid grid-cols-1 gap-4"
        data-testid="co-living-document-rename-form"
        @submit.prevent="onRename"
      >
        <FormField
          :label="$t('CO_LIVING.CONDOS.DOCUMENTS.COLUMNS.NAME')"
          for-id="co-living-document-title"
          :error="renameError"
          :hint="$t('CO_LIVING.CONDOS.DOCUMENTS.RENAME_HINT')"
        >
          <input
            id="co-living-document-title"
            v-model="renameTitle"
            type="text"
            maxlength="200"
            :class="fieldClass"
            data-testid="co-living-document-title"
          />
        </FormField>
        <div class="flex justify-end gap-3">
          <woot-button variant="clear" type="button" @click="renaming = null">
            {{ $t('CO_LIVING.COMMON.CANCEL') }}
          </woot-button>
          <woot-button
            type="submit"
            :is-loading="isRenaming"
            data-testid="co-living-document-rename-save"
          >
            {{ $t('CO_LIVING.COMMON.SAVE') }}
          </woot-button>
        </div>
      </form>
    </BaseModal>

    <DeleteModal
      v-if="toDelete"
      :delete-name="toDelete.title"
      :is-loading="isDeleting"
      @close="toDelete = null"
      @confirm="confirmDelete"
    >
      <p class="mt-2 text-sm text-slate-500">
        {{ $t('CO_LIVING.CONDOS.DOCUMENTS.DELETE_NOTE') }}
      </p>
    </DeleteModal>
  </section>
</template>
